import React from "react";
import Chart from "react-apexcharts";

interface Data {
  particuliers: {
    data: number[],
    labels: string[],
  };
  professionnels: {
    data: number[],
    labels: string[],
  };
}

interface ApexChartProps {
  data: Data;
  height?: string | number;
  width?: string | number;
  showLegend?: boolean;
  strokeWidth?: number;
}

const ApexChart: React.FC<ApexChartProps> = ({
  data,
  height = "100%",
  width = "100%",
  showLegend = true,
  strokeWidth = 6,
}) => {
  // Sélection intelligente des labels en fonction des données disponibles
  let labels = [];

  // Si les particuliers ont des données, utiliser leurs labels
  if (data.particuliers.data && data.particuliers.data.length > 0) {
    labels = data.particuliers.labels;
  }
  // Sinon, si les professionnels ont des données, utiliser leurs labels
  else if (data.professionnels.data && data.professionnels.data.length > 0) {
    labels = data.professionnels.labels;
  }
  // Si aucune série n'a de données, utiliser un tableau vide

  // Préparer les séries à afficher
  const series = [];

  // N'ajouter la série "Particuliers" que si elle contient des données
  if (data.particuliers.data && data.particuliers.data.length > 0) {
    series.push({
      name: "Particuliers",
      data: data.particuliers.data,
      color: "#f97316",
    });
  }

  // N'ajouter la série "Professionnels" que si elle contient des données
  if (data.professionnels.data && data.professionnels.data.length > 0) {
    series.push({
      name: "Professionnels",
      data: data.professionnels.data,
      color: "#0b92fc",
    });
  }

  // Déterminer si la légende doit être affichée
  const shouldShowLegend = showLegend && series.length > 1;

  const options = {
    chart: {
      type: "area",
      height,
      width,
      fontFamily: "Inter, sans-serif",
      dropShadow: {
        enabled: true,
      },
      toolbar: {
        show: false,
      },
    },
    xaxis: {
      show: true,
      categories: labels,
      labels: {
        show: true,
        style: {
          fontFamily: "Inter, sans-serif",
          cssClass: "text-xs font-normal fill-gray-500",
        },
      },
      axisBorder: {
        show: true,
      },
      axisTicks: {
        show: true,
      },
    },
    yaxis: {
      title: {
        text: "Chiffre d'affaire",
      },
      show: true,
      labels: {
        show: true,
        style: {
          fontFamily: "Inter, sans-serif",
          cssClass: "text-xs font-normal fill-gray-500",
        },
        formatter: function (value: number) {
          // Formater avec des séparateurs de milliers
          return value != null ? value.toLocaleString("fr-FR") : "0";
        },
      },
    },
    tooltip: {
      enabled: true,
      x: {
        show: false,
      },
      y: {
        formatter: function (value: number) {
          // Formater avec des séparateurs de milliers et ajouter FCFA
          return value != null
            ? `${value.toLocaleString("fr-FR")} FCFA`
            : "0 FCFA";
        },
      },
    },
    fill: {
      type: "gradient",
      gradient: {
        opacityFrom: 0.35,
        opacityTo: 0,
        shade: "#f97316",
        gradientToColors: ["#0b92fc"],
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      width: strokeWidth,
    },
    legend: {
      // N'afficher la légende que s'il y a plus d'une série et que showLegend est true
      show: shouldShowLegend,
      position: "top",
      horizontalAlign: "right",
      fontSize: "14px",
      markers: {
        width: 12,
        height: 12,
        radius: 12,
      },
    },
    grid: {
      show: false,
    },
  };

  return (
    <Chart
      options={options}
      series={series}
      type="area"
      height={height}
      width={width}
      className="h-full w-full"
    />
  );
};

export default ApexChart;
